/**
 * ZNotificationPreferences - Zod Validation Schemas for Notification Preferences API
 *
 * Endpoints: /api/notification-preferences
 * Controller: notification-preferences/infrastructure/controllers/notification-preferences.controller.ts
 */

import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// SHARED SCHEMAS
// ═══════════════════════════════════════════════════════════════

/** The 16 per-event toggles, shared between the response and the update body. */
const NotificationFlagsSchema = z.object({
  billingAlerts: z.boolean(),
  inboxNewConversation: z.boolean(),
  inboxConversationStatus: z.boolean(),
  inboxAssignedToMe: z.boolean(),
  orgMemberJoined: z.boolean(),
  orgMemberLeft: z.boolean(),
  agentCreationComplete: z.boolean(),
  agentCreationFailed: z.boolean(),
  deploymentConnectionError: z.boolean(),
  documentProcessingComplete: z.boolean(),
  workflowExecutionFailed: z.boolean(),
  orgMemberRoleChanged: z.boolean(),
  vibeSoftLimitWarning: z.boolean(),
  monthlyReport: z.boolean(),
  weeklyReport: z.boolean(),
  weeklyAgentPerformanceReport: z.boolean()
});

const NotificationPreferencesSchema = NotificationFlagsSchema.extend({
  /** Null when the user has never saved preferences (server-side defaults). */
  id: z.string().nullable(),
  userId: z.string(),
  organizationId: z.string(),
  emailTarget: z.enum(DbEnum.NOTIFICATION_EMAIL_TARGET_VALUES),
  customEmail: z.string().nullable(),
  createdAt: z.string(), // ISO string
  updatedAt: z.string() // ISO string
});

export type NotificationPreferences = z.infer<typeof NotificationPreferencesSchema>;

const UpdateNotificationPreferencesBodySchema = NotificationFlagsSchema.extend({
  emailTarget: z.enum(DbEnum.NOTIFICATION_EMAIL_TARGET_VALUES),
  customEmail: z.string().email().optional()
}).refine(
  (data) =>
    data.emailTarget !== DbEnum.NotificationEmailTarget.CUSTOM_EMAIL ||
    (data.customEmail !== undefined && data.customEmail.length > 0),
  { message: "customEmail is required when emailTarget is CUSTOM_EMAIL", path: ["customEmail"] }
);

export type UpdateNotificationPreferencesBody = z.infer<
  typeof UpdateNotificationPreferencesBodySchema
>;

// ═══════════════════════════════════════════════════════════════
// ZNOTIFICATION PREFERENCES MANIFEST
// ═══════════════════════════════════════════════════════════════

export const ZNotificationPreferences = {
  /**
   * GET /api/notification-preferences
   * Returns the current user's preferences for the current organization,
   * or server-side defaults (id = null) when none are saved yet.
   */
  Get: {
    method: "GET" as const,
    path: "/api/notification-preferences",
    Response: ApiSuccessSchema(NotificationPreferencesSchema)
  },

  /**
   * PUT /api/notification-preferences
   * Upsert the current user's preferences for the current organization.
   */
  Update: {
    method: "PUT" as const,
    path: "/api/notification-preferences",
    Body: UpdateNotificationPreferencesBodySchema,
    Response: ApiSuccessSchema(NotificationPreferencesSchema)
  }
} as const;
